import { useState, Fragment } from 'react';
import { Layout } from '@/components/Layout';
import { useBusinessUnits } from '@/hooks/useBusinessUnits';
import { useContracts } from '@/hooks/useContracts';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { Contract, VolumeDistribution } from '@/types';

export default function BusinessUnits() {
  const [expandedBuId, setExpandedBuId] = useState<number | null>(null);
  const navigate = useNavigate();

  const { data: businessUnits, isLoading: isLoadingBUs } = useBusinessUnits();
  const { data: contractsData, isLoading: isLoadingContracts } = useContracts({ pageSize: 1000 });

  const contracts = contractsData?.items || [];

  if (isLoadingBUs || isLoadingContracts) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Calculate volume and contracts for each BU
  const businessUnitsData = (businessUnits || []).map((bu) => {
    const buContracts = contracts.filter((c: Contract) => {
      if (c.businessUnitMode === 'Single') {
        return c.businessUnitName === bu.name;
      } else {
        return c.volumeDistributions?.some((vd: VolumeDistribution) => vd.businessUnitName === bu.name);
      }
    });
    
    const totalVolume = buContracts.reduce((sum: number, contract: Contract) => {
      if (contract.businessUnitMode === 'Single') {
        return sum + contract.totalVolume / 1000; // Convert to tons
      } else {
        const vd = contract.volumeDistributions?.find((vd: VolumeDistribution) => vd.businessUnitName === bu.name);
        return sum + (vd?.volumeKgs || 0) / 1000; // Convert to tons
      }
    }, 0);

    return {
      ...bu,
      contractCount: buContracts.length,
      totalVolume,
    };
  });

  // Sort by total volume descending
  const sortedBusinessUnits = businessUnitsData.sort((a, b) => b.totalVolume - a.totalVolume);
  
  // Calculate total volume across all BUs for percentage calculation
  const totalVolumeAll = sortedBusinessUnits.reduce((sum, bu) => sum + bu.totalVolume, 0);

  const toggleRow = (buId: number) => {
    setExpandedBuId(expandedBuId === buId ? null : buId);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Business Units</h1>
          <p className="text-muted-foreground mt-1">Manage business unit information</p>
        </div>

        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Active Contracts</TableHead>
                <TableHead className="text-right">Total Volume (t)</TableHead>
                <TableHead className="text-right">Percentage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBusinessUnits.map((bu, index) => {
                const isExpanded = expandedBuId === bu.id;
                const percentage = totalVolumeAll > 0 ? (bu.totalVolume / totalVolumeAll) * 100 : 0;
                const buContracts = contracts.filter((c: Contract) => {
                  if (c.businessUnitMode === 'Single') {
                    return c.businessUnitName === bu.name;
                  } else {
                    return c.volumeDistributions?.some((vd: VolumeDistribution) => vd.businessUnitName === bu.name);
                  }
                });

                return (
                  <Fragment key={bu.id}>
                    <TableRow 
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleRow(bu.id)}
                    >
                      <TableCell>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-medium">{bu.name}</TableCell>
                      <TableCell>{bu.code}</TableCell>
                      <TableCell>{bu.location}</TableCell>
                      <TableCell className="text-right">{bu.contractCount}</TableCell>
                      <TableCell className="text-right font-semibold text-primary">
                        {bu.totalVolume.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {percentage.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                    
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0 bg-muted/20">
                          <div className="p-4">
                            <h4 className="font-semibold mb-3">Contracts for {bu.name}</h4>
                            <div className="rounded-md border bg-card">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Product</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>Contract Date</TableHead>
                                    <TableHead className="text-right">Total Volume</TableHead>
                                    <TableHead className="text-right">BU Allocation</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {buContracts.map((contract: Contract) => {
                                    let buVolume = 0;
                                    
                                    if (contract.businessUnitMode === 'Single') {
                                      buVolume = contract.totalVolume / 1000;
                                    } else {
                                      const vd = contract.volumeDistributions?.find((vd: VolumeDistribution) => vd.businessUnitName === bu.name);
                                      buVolume = (vd?.volumeKgs || 0) / 1000;
                                    }

                                    const percentage = contract.totalVolume > 0 ? (buVolume * 1000 / contract.totalVolume) * 100 : 0;
                                    
                                     return (
                                      <TableRow 
                                        key={contract.id}
                                        className="cursor-pointer hover:bg-muted/80"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/contracts/${contract.id}`);
                                        }}
                                      >
                                        <TableCell className="font-medium">
                                          {contract.productName}
                                        </TableCell>
                                        <TableCell>{contract.supplierName}</TableCell>
                                        <TableCell>
                                          {format(new Date(contract.contractDate), 'dd MMM yyyy')}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {(contract.totalVolume / 1000).toFixed(1)}t
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {buVolume.toFixed(2)}t ({percentage.toFixed(2)}%)
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
